import yup from '@/utils/yup.locale';
import { yupResolver } from '@hookform/resolvers/yup';
import axios, { AxiosResponse } from 'axios';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';

import AtCautionLabel from '@/components/atoms/AtCautionLabel';
import AtErrorLabel from '@/components/atoms/AtErrorLabel';
import AtLink from '@/components/atoms/AtLink';
import MlLabeledMailBox from '@/components/molecules/MlLabeledMailBox';
import MlLabeledPasswordBox from '@/components/molecules/MlLabeledPasswordBox';
import OrContentHeadingArea from '@/components/organisms/OrContentHeadingArea';
import TemplateNoLogin from '@/components/templates/TemplateNoLogin';

import { LoginUserInfoState } from '@/states/LoginUserInfoState';
import { LoginResponse } from '@/types/api/authn/login';
import { CustomPageProps } from '@/types/client/CutostomPageProps';

import OrButtonAreaNoBackLinkLoading from '@/components/organisms/OrButtonAreaNoBackLinkLoading';
import { setCredentials, setSessionStartTime } from '@/utils/cognito';
import { ApiUrl, ExternalLink } from '@/utils/constants';

/**
 * メイン処理
 */
const AccountLogin: NextPage<CustomPageProps> = () => {
  const router = useRouter();
  // ログインユーザー情報セット処理
  const setLoginUserInfo = useSetRecoilState(LoginUserInfoState);
  // エラーメッセージ（ログインエラー）
  const [pageErrorMsg, setPageErrorMsg] = useState('');
  // 処理中
  const [isProcessing, setIsProcessing] = useState(false);

  // 画面項目定義
  type FormModel = {
    mailAddress: string;
    password: string;
  };

  // 入力チェック設定
  const schema: yup.ObjectSchema<FormModel> = yup.object().shape({
    mailAddress: yup.string().label('ログインID').required().max(256),
    password: yup.string().label('パスワード').required(),
  });

  // React Hook Form の設定
  type Form = yup.InferType<typeof schema>;
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<Form>({
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  /**
   * ログインボタン押下
   */
  const clickLoginButton = async () => {
    // エラーメッセージを初期化
    setPageErrorMsg('');
    setIsProcessing(true);

    // ログインAPI URL
    const apiUrl = `${process.env.NEXT_PUBLIC_BFF_URL}${ApiUrl.NonProtected.LOGIN}`;

    // パラメータの設定
    const params = {
      mail_address: getValues('mailAddress'),
      password: getValues('password'),
    };
    // httpヘッダの設定
    const config = {
      headers: { 'content-type': 'application/json' },
    };

    // ログイン処理API
    await axios
      .post(apiUrl, params, config)
      .then((res: AxiosResponse<LoginResponse>) => {
        // クレデンシャル情報をsessionstorageに保持
        setCredentials(res.data);
        setSessionStartTime();
        // ユーザー情報をセッションに保存
        setLoginUserInfo({ username: getValues('mailAddress') });
        localStorage.setItem('firstOpenFlg', '0');
        router.push('/');
      })
      .catch(() => {
        setPageErrorMsg('ログインID、または、パスワードが異なります');
        setValue('mailAddress', '');
        setValue('password', '');
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  /**
   * 画面レイアウト
   */
  return (
    <TemplateNoLogin>
      <OrContentHeadingArea>ログイン</OrContentHeadingArea>
      <div className='formArea'>
        <div className='taCenter'>
          <div>
            <MlLabeledMailBox
              name='mailAddress'
              id='mailAddress'
              labelClassName='w90'
              inputClassName='w250'
              register={register('mailAddress')}
            >
              ログインID
            </MlLabeledMailBox>
          </div>
          <AtErrorLabel testid='error_mailAddress'>{errors.mailAddress?.message}</AtErrorLabel>

          <div className='mt20'>
            <MlLabeledPasswordBox
              name='password'
              id='password'
              labelClassName='w90'
              inputClassName='w250'
              register={register('password')}
            >
              パスワード
            </MlLabeledPasswordBox>
          </div>
          <AtErrorLabel testid='error_password'>{errors.password?.message}</AtErrorLabel>

          <p>
            <AtLink href='/account/password-resend'>パスワードをお忘れの方はこちら</AtLink>
          </p>
          <p>
            <AtLink href={ExternalLink.FAQ_LOGIN_PROCEDURE} target='_blank'>
              マイページへのログイン方法
            </AtLink>
          </p>
        </div>
      </div>
      <OrButtonAreaNoBackLinkLoading
        mainLabel='ログイン'
        onClick={handleSubmit(clickLoginButton)}
        isLoading={isProcessing}
        disabled={isProcessing}
      />
      {pageErrorMsg && (
        <div className='taCenter mt4'>
          <AtErrorLabel testid='error_page_error'>{pageErrorMsg}</AtErrorLabel>
        </div>
      )}
      <p>
        <AtCautionLabel>
          注意：マイページのログインIDはご登録のメールアドレスです。MANOMAアプリのアカウント、パスワードとは異なります。
        </AtCautionLabel>
      </p>
      <p>
        <AtCautionLabel>
          初回ログインに際しましては、ご登録のメールアドレスに送信された「「MANOMA」マイページアカウントのご案内」にログイン方法が記載されておりますのでご確認ください。
        </AtCautionLabel>
      </p>
    </TemplateNoLogin>
  );
};
/* 画面タイトル設定 */
AccountLogin.getInitialProps = () => {
  return { title: 'ログイン' };
};
export default AccountLogin;
